import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { slugify } from "./slugify";
import "./Product.css";

const DEFAULT_IMG = "/products/default-category.png";

// Decorative, ambient color layer that lives behind the card grid so
// each card's backdrop-filter blur genuinely refracts saturated color —
// this is the actual mechanism behind the "liquid glass" look, not just
// a tinted panel. Purely decorative, so it's hidden from screen readers.
const GlassBlobField = () => (
  <div className="glass-blob-field" aria-hidden="true">
    <span className="glass-blob glass-blob--a" />
    <span className="glass-blob glass-blob--b" />
    <span className="glass-blob glass-blob--c" />
    <span className="glass-blob glass-blob--d" />
  </div>
);

// Section-wide ambient wash — larger, slower color blobs spanning the
// full section (heading through card row), a few quiet floating light
// motes for depth, and a hair of grain so the wash reads as tactile
// frosted glass rather than a flat gradient. Purely decorative.
const AuroraField = () => (
  <>
    <div className="aurora-layer" aria-hidden="true">
      <span className="aurora-blob aurora-blob--1" />
      <span className="aurora-blob aurora-blob--2" />
      <span className="aurora-blob aurora-blob--3" />
      <span className="aurora-blob aurora-blob--4" />

      <span
        className="aurora-mote"
        style={{ width: 5, height: 5, left: "12%", top: "70%", animationDuration: "14s", animationDelay: "0s" }}
      />
      <span
        className="aurora-mote"
        style={{ width: 3, height: 3, left: "28%", top: "55%", animationDuration: "18s", animationDelay: "3s" }}
      />
      <span
        className="aurora-mote"
        style={{ width: 4, height: 4, left: "48%", top: "80%", animationDuration: "16s", animationDelay: "6s" }}
      />
      <span
        className="aurora-mote"
        style={{ width: 3, height: 3, left: "66%", top: "60%", animationDuration: "20s", animationDelay: "2s" }}
      />
      <span
        className="aurora-mote"
        style={{ width: 5, height: 5, left: "82%", top: "72%", animationDuration: "15s", animationDelay: "8s" }}
      />
      <span
        className="aurora-mote"
        style={{ width: 4, height: 4, left: "90%", top: "40%", animationDuration: "17s", animationDelay: "4s" }}
      />
    </div>
    <div className="grain-overlay" aria-hidden="true" />
  </>
);

const ProductCard = ({ item, onExplore }) => {
  return (
    <div className="product-card">
      <div className="product-card__ring" />
      <div className="card-top">
        <img
          src={item.img || DEFAULT_IMG}
          alt={item.title}
          className="prod-image"
          onError={(e) => (e.target.src = DEFAULT_IMG)}
        />
      </div>
      <div className="card-bottom">
        <h3 className="product-title">{item.title}</h3>
        <p className="product-desc">{item.desc}</p>
        <div className="product-divider" />
        <div className="product-cta" onClick={() => onExplore(item)}>
          <span className="product-cta-text">Explore</span>
          <span className="product-cta-arrow">→</span>
        </div>
      </div>
    </div>
  );
};

// Subcategory Modal
const SubCategoryModal = ({ category, onSelect, onClose }) => {
  return (
    <AnimatePresence>
      {category && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-content"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">{category.title}</h2>
              <button className="modal-close" onClick={onClose}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-subtitle">Select a subcategory</p>
              <div className="subcategory-list">
                {category.subCategories.map((subCat, idx) => (
                  <motion.button
                    key={subCat.id}
                    className="subcat-item"
                    onClick={() => onSelect(category, subCat)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    whileHover={{ x: 8 }}
                  >
                    <span className="subcat-name">
                      {subCat.subCategoryName}
                    </span>
                    <span className="subcat-arrow">→</span>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: -22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const ornAnim = {
  hidden: { opacity: 0, scaleX: 0 },
  visible: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: 0.7, delay: 0.35, ease: "easeOut" },
  },
};

const Product = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isCancelled = false;

    const loadCategories = async () => {
      try {
        const categorySnap = await getDocs(collection(db, "product_categories"));

        const entries = await Promise.all(
          categorySnap.docs.map(async (categoryDoc) => {
            const title = categoryDoc.data().categoryName;
            if (!title) return null;

            const subSnap = await getDocs(
              collection(db, "product_categories", categoryDoc.id, "subCategories")
            );

            const subCategories = subSnap.docs.map((doc) => ({
              id: doc.id,
              subCategoryName: doc.data().subCategoryName || doc.id,
            }));

            const img = categoryDoc.data().categoryImageUrl || DEFAULT_IMG;
            const desc =
              categoryDoc.data().categoryDescription ||
              `Explore our ${title} range.`;

            return {
              id: categoryDoc.id,
              title,
              img,
              desc,
              subCategories,
            };
          })
        );

        if (!isCancelled) {
          setCategories(entries.filter(Boolean));
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };

    loadCategories();
    return () => {
      isCancelled = true;
    };
  }, []);

  const handleExplore = (category) => {
    if (category.subCategories.length === 1) {
      // Single subcategory → direct navigation
      const subCat = category.subCategories[0];
      navigate(
        `/category/${slugify(category.title)}/${slugify(subCat.subCategoryName)}`
      );
    } else if (category.subCategories.length > 1) {
      // Multiple subcategories → show modal
      setSelectedCategory(category);
    }
  };

  const handleSubcategorySelect = (category, subCat) => {
    setSelectedCategory(null);
    navigate(
      `/category/${slugify(category.title)}/${slugify(subCat.subCategoryName)}`
    );
  };

  return (
    <section className="product-section">
      <AuroraField />
      <div className="ps-container">
        {/* ── Heading ── */}
        <motion.div
          className="product-heading-wrap"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <motion.h2
            className="product-heading"
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
          >
            Product <span>Category</span>
          </motion.h2>
          <motion.div className="product-heading-ornament" variants={ornAnim}>
            <div className="orn-line-l" />
            <div className="orn-gem" />
            <div className="orn-line-r" />
          </motion.div>
        </motion.div>

        {/* ── Carousel Container ── */}
        <div className="carousel-wrapper">
          <GlassBlobField />

          {/* All categories share one row on desktop (see .product-grid);
              narrower screens wrap into the usual column counts. */}
          <div className="product-grid">
            {categories.map((item, i) => (
              <motion.div
                key={item.id}
                className="product-grid-item"
                initial={{ opacity: 0.98, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: i * 0.04, ease: "easeOut" }}
              >
                <ProductCard
                  item={item}
                  onExplore={handleExplore}
                />
              </motion.div>
            ))}
          </div>

        </div>
      </div>

      {/* ── Subcategory Selection Modal ── */}
      <SubCategoryModal
        category={selectedCategory}
        onSelect={handleSubcategorySelect}
        onClose={() => setSelectedCategory(null)}
      />
    </section>
  );
};

export default Product;